(function () {
  'use strict';

  var SECTION_SELECTOR = '.oddit-section-search-drawer';
  var DEBOUNCE_MS = 300;
  var MIN_QUERY_LENGTH = 2;
  // Max items shown in the left "Suggestions" column (collections + articles
  // + pages combined). Products are capped separately by the API limit.
  var SUGGESTIONS_LIMIT = 7;

  // Module-level state. Only one search drawer can be active because the
  // section schema declares `limit: 1`, but we still guard for the cases
  // where the section may not yet be in the DOM (theme editor reload).
  var activeSection = null;
  var triggerElement = null;
  var debounceTimer = null;
  var currentController = null;
  var escapeBound = false;

  // -------------------------------------------------------------------
  // Utilities
  // -------------------------------------------------------------------

  function escapeHtml(str) {
    var div = document.createElement('div');
    div.appendChild(document.createTextNode(str == null ? '' : String(str)));
    return div.innerHTML;
  }

  function getSection() {
    return activeSection || document.querySelector(SECTION_SELECTOR);
  }

  // -------------------------------------------------------------------
  // Initial state vs. API results
  //
  // The Liquid renders the "initial state" inside `.results`:
  //   - the popular searches list   (left column)
  //   - the featured products grid  (right column, + pagination + shop-all)
  //
  // When the user types a query >= MIN_QUERY_LENGTH chars we hide those
  // initial blocks and show the predictive-search results, which reuse the
  // same two-column layout:
  //   - SUGGESTIONS (left)  — collection / article / page links
  //   - PRODUCTS    (right) — image + title rows
  // The results nodes carry the initial layout classes (`__searches-section`,
  // `__divider`, `__products-section`) so they pick up the existing grid
  // areas, and are appended once on first use. Clearing the query reverses
  // the swap.
  // -------------------------------------------------------------------

  function getInitialStateNodes(section) {
    return {
      searches: section.querySelector('.oddit-section-search-drawer__searches-section:not([data-search-suggestions-section])'),
      divider: section.querySelector('.oddit-section-search-drawer__divider:not([data-search-divider])'),
      products: section.querySelector('.oddit-section-search-drawer__products-section:not([data-search-products-section])'),
      shopAll: section.querySelector('.oddit-section-search-drawer__shop-all')
    };
  }

  function getResultNodes(section) {
    return {
      suggestionsSection: section.querySelector('[data-search-suggestions-section]'),
      suggestionsList: section.querySelector('[data-search-suggestions]'),
      divider: section.querySelector('[data-search-divider]'),
      productsSection: section.querySelector('[data-search-products-section]'),
      productsList: section.querySelector('[data-search-products]'),
      message: section.querySelector('[data-search-message]')
    };
  }

  function showInitialState(section) {
    var initial = getInitialStateNodes(section);
    Object.keys(initial).forEach(function (key) {
      if (initial[key]) initial[key].style.display = '';
    });
    var r = getResultNodes(section);
    [r.suggestionsSection, r.divider, r.productsSection, r.message].forEach(function (node) {
      if (node) node.style.display = 'none';
    });
  }

  function hideInitialState(section) {
    var initial = getInitialStateNodes(section);
    Object.keys(initial).forEach(function (key) {
      if (initial[key]) initial[key].style.display = 'none';
    });
  }

  function ensureResultsScaffold(section) {
    var existing = section.querySelector('[data-search-scaffold]');
    if (existing) return existing;

    var results = section.querySelector('.oddit-section-search-drawer__results');
    if (!results) return null;

    // Suggestions column (left) — reuses the popular-searches layout classes
    // so it inherits the grid area and spacing from the initial state.
    var suggestions = document.createElement('div');
    suggestions.className = 'oddit-section-search-drawer__searches-section';
    suggestions.setAttribute('data-search-suggestions-section', '');
    suggestions.setAttribute('data-search-scaffold', '');
    suggestions.style.display = 'none';
    suggestions.innerHTML =
      '<h3 class="oddit-section-search-drawer__heading">Suggestions</h3>' +
      '<ul class="oddit-section-search-drawer__searches-list" data-search-suggestions></ul>';

    // Results divider — the initial divider sits between the initial columns
    // in DOM order, which would mis-place it on the mobile (stacked) layout,
    // so the results view uses its own divider in the correct position.
    var divider = document.createElement('div');
    divider.className = 'oddit-section-search-drawer__divider';
    divider.setAttribute('data-search-divider', '');
    divider.setAttribute('role', 'presentation');
    divider.setAttribute('aria-hidden', 'true');
    divider.style.display = 'none';

    // Products column (right) — image + title rows.
    var products = document.createElement('div');
    products.className = 'oddit-section-search-drawer__products-section';
    products.setAttribute('data-search-products-section', '');
    products.style.display = 'none';
    products.innerHTML =
      '<h3 class="oddit-section-search-drawer__heading">Products</h3>' +
      '<div class="oddit-section-search-drawer__result-products" data-search-products></div>';

    // Loading / empty / error message (spans both columns).
    var message = document.createElement('div');
    message.className = 'oddit-section-search-drawer__message';
    message.setAttribute('data-search-message', '');
    message.setAttribute('role', 'status');
    message.setAttribute('aria-live', 'polite');
    message.style.display = 'none';

    results.appendChild(suggestions);
    results.appendChild(divider);
    results.appendChild(products);
    results.appendChild(message);

    return suggestions;
  }

  // -------------------------------------------------------------------
  // Loading / empty / error state helpers
  // -------------------------------------------------------------------

  // Show a single full-width message (empty / error) and hide both result
  // columns + divider. `srOnly` keeps the text for assistive tech only —
  // used by the loading state, which shows a skeleton instead.
  function showMessage(section, text, srOnly) {
    ensureResultsScaffold(section);
    hideInitialState(section);
    var r = getResultNodes(section);
    if (!srOnly) {
      if (r.suggestionsSection) r.suggestionsSection.style.display = 'none';
      if (r.productsSection) r.productsSection.style.display = 'none';
      if (r.divider) r.divider.style.display = 'none';
    }
    if (r.message) {
      r.message.textContent = text;
      r.message.className = 'oddit-section-search-drawer__message'
        + (srOnly ? ' oddit-section-search-drawer__visually-hidden' : '');
      r.message.style.display = '';
    }
  }

  // Skeleton placeholders shown while a request is in flight. They mirror the
  // results layout so the drawer keeps its height instead of collapsing to a
  // single line, and inherit the section's typography/spacing.
  function renderSkeletonSuggestions() {
    var widths = ['80%', '64%', '72%', '56%', '70%', '60%', '74%'];
    return widths.map(function (w) {
      return '<li class="oddit-section-search-drawer__search-item" aria-hidden="true">'
        + '<span class="oddit-section-search-drawer__skeleton oddit-section-search-drawer__skeleton--line" style="width:' + w + '"></span>'
        + '</li>';
    }).join('');
  }

  function renderSkeletonProducts() {
    var card = ''
      + '<article class="oddit-section-search-drawer__product-card" aria-hidden="true">'
      +   '<div class="oddit-section-search-drawer__product-image-wrap oddit-section-search-drawer__skeleton"></div>'
      +   '<div class="oddit-section-search-drawer__product-details">'
      +     '<span class="oddit-section-search-drawer__skeleton oddit-section-search-drawer__skeleton--line" style="width:90%"></span>'
      +     '<span class="oddit-section-search-drawer__skeleton oddit-section-search-drawer__skeleton--line" style="width:40%"></span>'
      +   '</div>'
      + '</article>';
    var cards = '';
    for (var i = 0; i < 4; i++) cards += card;
    return cards;
  }

  function showLoadingState(section) {
    ensureResultsScaffold(section);
    hideInitialState(section);
    var r = getResultNodes(section);

    if (r.suggestionsList) r.suggestionsList.innerHTML = renderSkeletonSuggestions();
    if (r.productsList) r.productsList.innerHTML = renderSkeletonProducts();
    if (r.suggestionsSection) r.suggestionsSection.style.display = '';
    if (r.productsSection) r.productsSection.style.display = '';
    if (r.divider) r.divider.style.display = '';

    // Announce loading to assistive tech without a visible lone word.
    showMessage(section, 'Searching…', true);
  }

  function showErrorState(section) {
    showMessage(section, 'Something went wrong. Please try again.');
  }

  // -------------------------------------------------------------------
  // Result rendering — `renderResults` is the single DOM orchestrator,
  // mirroring the cart drawer's `renderCart` pattern. Per-resource
  // helpers return HTML strings only.
  // -------------------------------------------------------------------

  // Wrap the portion of `title` matching `query` in <strong> so the typed
  // term reads bolder than the rest, as in the design.
  function highlightMatch(title, query) {
    var safe = escapeHtml(title);
    if (!query) return safe;
    var idx = title.toLowerCase().indexOf(query.toLowerCase());
    if (idx === -1) return safe;
    return escapeHtml(title.slice(0, idx))
      + '<strong class="oddit-section-search-drawer__suggestion-match">'
      + escapeHtml(title.slice(idx, idx + query.length))
      + '</strong>'
      + escapeHtml(title.slice(idx + query.length));
  }

  // Suggestion = a collection / article / page link styled like the popular
  // searches list (no leading icon, per the design).
  function renderSuggestion(item, query) {
    return ''
      + '<li class="oddit-section-search-drawer__search-item">'
      +   '<a href="' + item.url + '" class="oddit-section-search-drawer__search-link">'
      +     '<span class="oddit-section-search-drawer__search-link-text">' + highlightMatch(item.title || '', query) + '</span>'
      +   '</a>'
      + '</li>';
  }

  function getProductImage(product) {
    if (product.image) return product.image;
    if (product.featured_image && product.featured_image.url) return product.featured_image.url;
    return '';
  }

  // Predictive search returns prices as decimal strings (e.g. "29.99"), not
  // cents — format with the store's currency symbol.
  function formatMoney(value) {
    var num = parseFloat(value);
    if (isNaN(num)) return '';
    return '$' + num.toFixed(2);
  }

  function renderProductPrice(product) {
    var min = product.price_min != null ? product.price_min : product.price;
    var max = product.price_max != null ? product.price_max : product.price;
    var varies = min != null && max != null && parseFloat(min) !== parseFloat(max);
    var amount = formatMoney(varies ? min : product.price);
    if (!amount) return '';
    return ''
      + '<p class="oddit-section-search-drawer__product-price">'
      +   (varies ? '<span class="oddit-section-search-drawer__product-price-from">from</span>' : '')
      +   '<span class="oddit-section-search-drawer__product-price-amount">' + amount + '</span>'
      + '</p>';
  }

  // Render a result using the featured-product card design (image + title +
  // price). Rating, badge and swatches aren't available from the predictive
  // search API, so they're omitted.
  function renderProductResult(product) {
    var src = getProductImage(product);
    var image = src
      ? '<img class="oddit-section-search-drawer__product-image" src="' + src + '" alt="' + escapeHtml(product.title) + '" width="190" height="226" loading="lazy">'
      : '';
    return ''
      + '<article class="oddit-section-search-drawer__product-card">'
      +   '<a href="' + product.url + '" class="oddit-section-search-drawer__product-link" aria-label="' + escapeHtml(product.title) + '">'
      +     '<div class="oddit-section-search-drawer__product-image-wrap">' + image + '</div>'
      +   '</a>'
      +   '<div class="oddit-section-search-drawer__product-details">'
      +     '<h4 class="oddit-section-search-drawer__product-title">' + escapeHtml(product.title) + '</h4>'
      +     renderProductPrice(product)
      +   '</div>'
      + '</article>';
  }

  function renderResults(section, results, query) {
    ensureResultsScaffold(section);
    hideInitialState(section);
    var r = getResultNodes(section);

    var products = (results && results.products) || [];
    var collections = (results && results.collections) || [];
    var articles = (results && results.articles) || [];
    var pages = (results && results.pages) || [];

    // Suggestions = the non-product resources, in a stable order, capped.
    var suggestions = [].concat(collections, articles, pages).slice(0, SUGGESTIONS_LIMIT);
    var totalResults = products.length + suggestions.length;

    if (totalResults === 0) {
      showMessage(section, 'No results found.');
      return;
    }

    if (r.message) r.message.style.display = 'none';

    // Suggestions column (left)
    if (r.suggestionsSection && r.suggestionsList) {
      if (suggestions.length) {
        r.suggestionsList.innerHTML = suggestions.map(function (item) {
          return renderSuggestion(item, query);
        }).join('');
        r.suggestionsSection.style.display = '';
      } else {
        r.suggestionsList.innerHTML = '';
        r.suggestionsSection.style.display = 'none';
      }
    }

    // Products column (right)
    if (r.productsSection && r.productsList) {
      if (products.length) {
        r.productsList.innerHTML = products.map(renderProductResult).join('');
        r.productsSection.style.display = '';
      } else {
        r.productsList.innerHTML = '';
        r.productsSection.style.display = 'none';
      }
    }

    // Divider only makes sense when both columns are populated.
    if (r.divider) {
      r.divider.style.display = (suggestions.length && products.length) ? '' : 'none';
    }
  }

  function clearResults(section) {
    if (!section) return;
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    clearTimeout(debounceTimer);

    var r = getResultNodes(section);
    if (r.suggestionsList) r.suggestionsList.innerHTML = '';
    if (r.productsList) r.productsList.innerHTML = '';

    showInitialState(section);
  }

  // -------------------------------------------------------------------
  // Predictive Search API fetch
  // -------------------------------------------------------------------

  function fetchPredictiveResults(section, query) {
    if (currentController) currentController.abort();
    currentController = new AbortController();

    showLoadingState(section);

    var types = section.getAttribute('data-search-resource-types') || 'product,collection,article,page';
    var limit = section.getAttribute('data-search-results-limit') || '4';
    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=' + encodeURIComponent(types)
      + '&resources[limit]=' + encodeURIComponent(limit)
      // limit_scope=each applies the limit per resource type; the default
      // (`all`) splits it across every type combined, which starves the
      // products column once suggestions share the budget.
      + '&resources[limit_scope]=each'
      + '&resources[options][unavailable_products]=last';

    fetch(url, {
      signal: currentController.signal,
      headers: { 'Content-Type': 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Predictive search request failed (' + res.status + ')');
        return res.json();
      })
      .then(function (data) {
        var results = (data && data.resources && data.resources.results) || {};
        renderResults(section, results, query);
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        showErrorState(section);
      });
  }

  function handleSearchInput(section, e) {
    var query = (e.target.value || '').trim();
    clearTimeout(debounceTimer);

    if (query.length < MIN_QUERY_LENGTH) {
      clearResults(section);
      return;
    }

    debounceTimer = setTimeout(function () {
      fetchPredictiveResults(section, query);
    }, DEBOUNCE_MS);
  }

  // -------------------------------------------------------------------
  // Drawer open / close
  // -------------------------------------------------------------------

  function lockBodyScroll() {
    if (window.OdditScrollLock) {
      window.OdditScrollLock.lock();
    } else {
      document.body.style.overflow = 'hidden';
    }
  }

  function unlockBodyScroll() {
    if (window.OdditScrollLock) {
      window.OdditScrollLock.unlock();
    } else {
      document.body.style.overflow = '';
    }
  }

  // Anchor the overlay just below the header (mobile) or the announcement bar
  // (desktop, where the header hides while search is open) so the panel drops
  // into the same place it always has.
  function setOverlayTop(section) {
    var onDesktop = window.matchMedia('(min-width: 990px)').matches;
    var anchor = document.querySelector(
      onDesktop ? '.oddit-announcement-bar' : '.oddit-header'
    );
    var top = anchor ? Math.max(anchor.getBoundingClientRect().bottom, 0) : 0;
    section.style.setProperty('--oddit-search-overlay-top', top + 'px');
  }

  function isOpen(section) {
    return !!section && section.getAttribute('aria-hidden') === 'false';
  }

  function openDrawer() {
    var section = getSection();
    if (!section) return;

    // Remember whatever the user was focused on (typically the header
    // search icon) so we can restore focus on close.
    if (document.activeElement && document.activeElement !== document.body) {
      triggerElement = document.activeElement;
    }

    setOverlayTop(section);
    section.style.display = '';
    section.setAttribute('aria-hidden', 'false');
    lockBodyScroll();

    var input = section.querySelector('[data-search-input]');
    if (input) {
      requestAnimationFrame(function () {
        try {
          input.focus({ preventScroll: true });
        } catch (e) {
          input.focus();
        }
      });
    }

    section.dispatchEvent(new CustomEvent('oddit:search-drawer:opened', { bubbles: true }));
  }

  function closeDrawer() {
    var section = getSection();
    if (!section) return;

    section.setAttribute('aria-hidden', 'true');
    section.style.display = 'none';
    unlockBodyScroll();

    // Reset input + results to initial state
    var input = section.querySelector('[data-search-input]');
    if (input) input.value = '';
    clearResults(section);

    if (triggerElement && typeof triggerElement.focus === 'function') {
      try {
        triggerElement.focus({ preventScroll: true });
      } catch (e) {
        triggerElement.focus();
      }
    }
    triggerElement = null;

    section.dispatchEvent(new CustomEvent('oddit:search-drawer:closed', { bubbles: true }));
  }

  function toggleDrawer() {
    var section = getSection();
    if (!section) return;
    if (isOpen(section)) {
      closeDrawer();
    } else {
      openDrawer();
    }
  }

  // Single global escape handler — covers all sections.
  function handleEscapeKey(e) {
    if (e.key !== 'Escape' && e.key !== 'Esc') return;
    var section = getSection();
    if (section && isOpen(section)) {
      e.preventDefault();
      closeDrawer();
    }
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function handleTabKey(e) {
    if (e.key !== 'Tab') return;
    var section = getSection();
    if (!section || !isOpen(section)) return;

    var nodes = section.querySelectorAll(FOCUSABLE);
    var items = [];
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].getClientRects().length) items.push(nodes[i]);
    }
    if (!items.length) return;

    var first = items[0];
    var last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    } else if (!section.contains(document.activeElement)) {
      e.preventDefault();
      first.focus();
    }
  }

  function bindEscapeOnce() {
    if (escapeBound) return;
    document.addEventListener('keydown', handleEscapeKey);
    document.addEventListener('keydown', handleTabKey);
    escapeBound = true;
  }

  // -------------------------------------------------------------------
  // Section initialization
  // -------------------------------------------------------------------

  function initSection(section) {
    if (!section || section.dataset.odditInitialized === 'true') return;
    section.dataset.odditInitialized = 'true';

    activeSection = section;

    // Initial accessibility state — section is hidden via inline
    // `display: none` from the Liquid template.
    if (!section.hasAttribute('aria-hidden')) {
      section.setAttribute('aria-hidden', 'true');
    }
    section.setAttribute('role', section.getAttribute('role') || 'dialog');
    section.setAttribute('aria-modal', 'true');

    var input = section.querySelector('.oddit-section-search-drawer__input');
    var closeBtn = section.querySelector('.oddit-section-search-drawer__close-btn');
    var form = section.querySelector('.oddit-section-search-drawer__search-form');

    // Pre-build the API-results scaffold so subsequent input is cheap.
    ensureResultsScaffold(section);

    // Focus the input when the drawer is rendered. If the drawer is
    // currently hidden this is a harmless no-op; `openDrawer()` will
    // re-issue the focus when the drawer is shown.
    if (input) {
      requestAnimationFrame(function () {
        try {
          input.focus({ preventScroll: true });
        } catch (e) {
          input.focus();
        }
      });

      // Debounced predictive search.
      input.addEventListener('input', function (e) {
        handleSearchInput(section, e);
      });
    }

    // Close button — also dispatches a custom event so a parent drawer
    // controller (e.g. the header) can react if it's listening.
    if (closeBtn) {
      closeBtn.addEventListener('click', function () {
        section.dispatchEvent(
          new CustomEvent('oddit:search-drawer:close', { bubbles: true })
        );
        closeDrawer();
      });
    }

    // Click the dimmed backdrop (anywhere outside the white panel) to close.
    section.addEventListener('click', function (e) {
      if (e.target === section) closeDrawer();
    });

    // Allow native form submission to /search?q= for graceful
    // degradation: if JS fails to load or fetch is blocked, pressing
    // Enter still navigates to the full search results page.
    if (form) {
      form.addEventListener('submit', function (e) {
        var q = input ? input.value.trim() : '';
        if (!q) {
          e.preventDefault();
        }
      });
    }

    // Swatch selection — preview-only simulation. Clicking a colour
    // swatch marks it active and clears the others in the same card.
    // The real implementation will swap the product variant/image
    // during the liquid-conversion phase.
    var swatchGroups = section.querySelectorAll(
      '.oddit-section-search-drawer__product-swatches'
    );
    swatchGroups.forEach(function (group) {
      var swatches = group.querySelectorAll(
        '.oddit-section-search-drawer__product-swatch'
      );
      group.addEventListener('click', function (e) {
        var clicked = e.target.closest(
          '.oddit-section-search-drawer__product-swatch'
        );
        if (!clicked || !group.contains(clicked)) return;
        swatches.forEach(function (s) {
          s.classList.toggle(
            'oddit-section-search-drawer__product-swatch--active',
            s === clicked
          );
        });
      });
    });

    // Mobile products carousel + pagination. The grid is a horizontal
    // scroll carousel on mobile (see CSS); the prev/next arrows scroll
    // it and the dots track the scroll position. Tablet/desktop render
    // a static grid and the pagination is hidden via CSS.
    var pagination = section.querySelector('.oddit-section-search-drawer__pagination');
    var carousel = section.querySelector('.oddit-section-search-drawer__products-grid');
    if (pagination && carousel) {
      var indicators = pagination.querySelector('.oddit-section-search-drawer__pagination-indicators');
      var prev = pagination.querySelectorAll('.oddit-section-search-drawer__pagination-btn')[0];
      var next = pagination.querySelectorAll('.oddit-section-search-drawer__pagination-btn')[1];
      var dots = [];

      function cardStep() {
        var card = carousel.querySelector(
          '.oddit-section-search-drawer__product-card'
        );
        return card ? card.getBoundingClientRect().width + 8 : carousel.clientWidth;
      }

      // One dot per single-card scroll stop: with N cards and V fully
      // visible there are N - V + 1 stops. This matches the arrows,
      // which scroll exactly one card per click. Rebuilt only when the
      // count changes (e.g. resize), so it adapts to products + screen.
      function renderDots() {
        if (!indicators || !carousel.clientWidth) return;
        var cards = carousel.querySelectorAll(
          '.oddit-section-search-drawer__product-card'
        ).length;
        var visible = Math.max(1, Math.round(carousel.clientWidth / cardStep()));
        var pageCount = Math.max(1, cards - visible + 1);
        if (pageCount === dots.length) return;
        indicators.innerHTML = '';
        dots = [];
        for (var i = 0; i < pageCount; i++) {
          var dot = document.createElement('span');
          dot.className = 'oddit-section-search-drawer__pagination-dot';
          indicators.appendChild(dot);
          dots.push(dot);
        }
      }

      function sync() {
        renderDots();
        var max = carousel.scrollWidth - carousel.clientWidth;
        var x = carousel.scrollLeft;

        // Active dot = current single-card scroll stop.
        if (dots.length) {
          var index = Math.min(
            dots.length - 1,
            Math.round(x / cardStep())
          );
          dots.forEach(function (d, i) {
            d.classList.toggle(
              'oddit-section-search-drawer__pagination-dot--active',
              i === index
            );
          });
        }

        // Disable the arrow that can't scroll any further (1px
        // tolerance for sub-pixel scroll positions). The CSS
        // :disabled rule drops the arrow to 0.5 opacity.
        if (prev) prev.disabled = x <= 1;
        if (next) next.disabled = x >= max - 1;
      }

      function page(direction) {
        // Scroll roughly one card-and-gap per click.
        var first = carousel.querySelector(
          '.oddit-section-search-drawer__product-card'
        );
        var step = first ? first.getBoundingClientRect().width + 8 : carousel.clientWidth;
        carousel.scrollBy({ left: direction * step, behavior: 'smooth' });
      }

      if (prev) {
        prev.addEventListener('click', function () {
          page(-1);
        });
      }
      if (next) {
        next.addEventListener('click', function () {
          page(1);
        });
      }
      carousel.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    }

    bindEscapeOnce();
  }

  function teardownSection(section) {
    if (!section) return;
    if (activeSection === section) activeSection = null;
    if (currentController) {
      currentController.abort();
      currentController = null;
    }
    clearTimeout(debounceTimer);
    unlockBodyScroll();
    delete section.dataset.odditInitialized;
  }

  function initAll() {
    var sections = document.querySelectorAll(SECTION_SELECTOR);
    sections.forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // -------------------------------------------------------------------
  // Theme editor lifecycle
  // -------------------------------------------------------------------

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var section = e.target.querySelector(SECTION_SELECTOR);
    if (section) teardownSection(section);
  });

  // -------------------------------------------------------------------
  // Public API — `window.OdditSearchDrawer` (PascalCase). The header
  // section's JS calls `window.OdditSearchDrawer.open()` to surface the
  // drawer when the user clicks the magnifier icon.
  // -------------------------------------------------------------------

  window.OdditSearchDrawer = {
    open: openDrawer,
    close: closeDrawer,
    toggle: toggleDrawer,
    isOpen: function () { return isOpen(getSection()); }
  };
})();
